import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getITVisit } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Printer, ArrowLeft, Shield } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { getITErrorMessage } from '../../lib/itErrors';
import { IT_CYBERSECURITY_CONTROLS, COMPLIANCE_STATUSES, mergeChecklistWithControls, computeComplianceSummary } from '../../lib/itComplianceControls';

export default function ServiceVisitReportPage() {
  const { visitId } = useParams();
  const navigate = useNavigate();
  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await getITVisit(visitId);
        if (mounted) setVisit(res.data);
      } catch (err) {
        toast.error(getITErrorMessage(err, 'Error cargando visita'));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [visitId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-gray-700">
        Cargando reporte...
      </div>
    );
  }

  if (!visit) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white text-gray-700 gap-3">
        <p>No se encontró la visita.</p>
        <Button onClick={() => navigate('/it/visits')} variant="outline"><ArrowLeft size={16} className="mr-1" /> Volver</Button>
      </div>
    );
  }

  const checklist = mergeChecklistWithControls(visit.cybersecurity_checklist);
  const compSummary = computeComplianceSummary(checklist);
  const hasCompliance = checklist.some(c => c.estado);
  const totals = (visit.assets || []).reduce((acc, va) => {
    acc.minutos += va.time_spent_min || 0;
    acc.costo += va.cost || 0;
    return acc;
  }, { minutos: 0, costo: 0 });

  return (
    <div className="min-h-screen bg-gray-100 text-gray-900 print:bg-white">
      {/* Toolbar (hidden on print) */}
      <div className="print:hidden bg-white border-b border-gray-200 p-3 flex items-center justify-between sticky top-0 z-10">
        <Button variant="ghost" onClick={() => navigate(`/it/visits/${visitId}`)} className="text-gray-700">
          <ArrowLeft size={16} className="mr-1" /> Volver al detalle
        </Button>
        <Button onClick={() => window.print()} className="bg-[#E2B05E] hover:bg-[#c9993f] text-black">
          <Printer size={16} className="mr-1" /> Imprimir / Guardar PDF
        </Button>
      </div>

      <div className="max-w-4xl mx-auto bg-white p-8 my-4 print:my-0 print:p-6 shadow print:shadow-none">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-gray-300 pb-5 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Shield weight="fill" className="text-[#E2B05E] w-7 h-7" />
              <span className="font-bold text-xl tracking-tight">VALHALLA</span>
              <span className="text-[10px] text-gray-500 tracking-widest uppercase">Corporations</span>
            </div>
            <p className="text-xs text-gray-500">Reporte de Servicio Técnico</p>
          </div>
          <div className="text-right text-xs">
            <div className="font-bold text-lg text-gray-900">{visit.visit_number}</div>
            <div className="text-gray-600">{(visit.scheduled_for || '').replace('T', ' · ').slice(0, 18)}</div>
            <div className="text-gray-500 capitalize">{(visit.tipo || '').replace(/_/g, ' ')} · {visit.priority}</div>
            <div className="mt-1 inline-block px-2 py-0.5 rounded border border-gray-300 capitalize">
              Estado: {(visit.status || '').replace(/_/g, ' ')}
            </div>
          </div>
        </div>

        {/* Cliente & Ubicación */}
        <section className="grid grid-cols-2 gap-6 mb-6 text-sm">
          <div>
            <h3 className="font-semibold text-gray-800 border-b border-gray-200 pb-1 mb-2">Cliente</h3>
            <div className="text-gray-900 font-medium">{visit.client_name || '—'}</div>
            {visit.location_name && <div className="text-gray-600 text-xs mt-0.5">{visit.location_name}</div>}
            {visit.recibe_nombre && <div className="text-gray-600 text-xs mt-1">Recibe: {visit.recibe_nombre}</div>}
          </div>
          <div>
            <h3 className="font-semibold text-gray-800 border-b border-gray-200 pb-1 mb-2">Técnicos</h3>
            {(visit.technicians || []).length === 0 ? (
              <div className="text-gray-500 text-xs italic">Sin técnicos asignados</div>
            ) : (
              <ul className="text-xs text-gray-700 space-y-0.5">
                {visit.technicians.map((t, i) => (
                  <li key={i}>
                    {t.employee_name || t.employee_id}
                    {t.role && <span className="text-gray-500"> · {t.role}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* Descripción / Resultado */}
        {visit.description && (
          <section className="mb-6">
            <h3 className="font-semibold text-gray-800 border-b border-gray-200 pb-1 mb-2">Descripción</h3>
            <p className="text-gray-700 text-xs whitespace-pre-wrap">{visit.description}</p>
          </section>
        )}

        {/* Activos / Máquinas */}
        <section className="mb-6">
          <h3 className="font-semibold text-gray-800 border-b border-gray-200 pb-1 mb-3">
            Máquinas atendidas ({(visit.assets || []).length})
          </h3>
          {(visit.assets || []).length === 0 ? (
            <p className="text-xs text-gray-500 italic">Sin activos registrados en esta visita.</p>
          ) : (
            <div className="space-y-3">
              {visit.assets.map((va, idx) => (
                <div key={va._id || idx} className="border border-gray-200 rounded p-3 text-xs break-inside-avoid">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-semibold text-gray-900 text-sm">
                        {idx + 1}. {va.asset_name || 'Activo'}
                      </div>
                      <div className="text-gray-600">
                        {va.asset_serial && <>S/N: {va.asset_serial} </>}
                        {va.asset_tag && <>· Tag: {va.asset_tag} </>}
                        {va.asset_category && <>· {va.asset_category}</>}
                      </div>
                    </div>
                    <div className="text-right text-gray-600">
                      {va.time_spent_min > 0 && <div>⏱ {va.time_spent_min} min</div>}
                      {va.cost > 0 && <div>${Number(va.cost).toFixed(2)}</div>}
                    </div>
                  </div>
                  <table className="w-full text-xs">
                    <tbody>
                      {va.reported_problem && (
                        <tr><td className="text-gray-500 align-top pr-2 py-0.5 w-28">Problema</td><td className="text-gray-800 py-0.5">{va.reported_problem}</td></tr>
                      )}
                      {va.diagnosis && (
                        <tr><td className="text-gray-500 align-top pr-2 py-0.5">Diagnóstico</td><td className="text-gray-800 py-0.5">{va.diagnosis}</td></tr>
                      )}
                      {va.work_performed && (
                        <tr><td className="text-gray-500 align-top pr-2 py-0.5">Acciones</td><td className="text-gray-800 py-0.5">{va.work_performed}</td></tr>
                      )}
                      {va.recommendation && (
                        <tr><td className="text-gray-500 align-top pr-2 py-0.5">Recomendación</td><td className="text-gray-800 py-0.5">{va.recommendation}</td></tr>
                      )}
                      {va.status_after && (
                        <tr><td className="text-gray-500 align-top pr-2 py-0.5">Estado final</td><td className="text-gray-800 py-0.5 capitalize">{(va.status_after || '').replace(/_/g, ' ')}</td></tr>
                      )}
                      {va.requires_followup && (
                        <tr><td className="text-gray-500 align-top pr-2 py-0.5">Seguimiento</td><td className="text-yellow-700 font-medium py-0.5">Sí — requiere seguimiento</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ))}
              {(totals.minutos > 0 || totals.costo > 0) && (
                <div className="flex justify-end text-xs text-gray-700 gap-6 border-t border-gray-200 pt-2 mt-2">
                  {totals.minutos > 0 && <span>Tiempo total: <strong>{totals.minutos} min</strong></span>}
                  {totals.costo > 0 && <span>Costo total servicio: <strong>${totals.costo.toFixed(2)}</strong></span>}
                </div>
              )}
            </div>
          )}
        </section>

        {/* Checklist operativo embebido */}
        {(visit.checklist || []).length > 0 && (
          <section className="mb-6">
            <h3 className="font-semibold text-gray-800 border-b border-gray-200 pb-1 mb-2">Checklist operativo</h3>
            <ul className="text-xs space-y-1">
              {visit.checklist.map((c, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className={`inline-block w-3 h-3 mt-0.5 border rounded ${c.completed ? 'bg-green-600 border-green-600' : 'border-gray-400'}`}></span>
                  <span className={c.completed ? 'text-gray-800' : 'text-gray-500'}>{c.item}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Adjuntos */}
        {(visit.attachments || []).length > 0 && (
          <section className="mb-6">
            <h3 className="font-semibold text-gray-800 border-b border-gray-200 pb-1 mb-2">Evidencia adjunta</h3>
            <ul className="text-xs space-y-1">
              {visit.attachments.map((a) => (
                <li key={a._id} className="text-gray-700">
                  <span className="text-gray-500 uppercase">[{a.kind || 'archivo'}{a.phase ? ` · ${a.phase}` : ''}]</span>{' '}
                  {a.name || 'Sin nombre'}
                  {a.description && <span className="text-gray-500"> — {a.description}</span>}
                  {a.url && <span className="text-gray-500 break-all"> · {a.url}</span>}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Ciberseguridad (si fue evaluado) */}
        {hasCompliance && (
          <section className="mb-6 break-before-page print:break-before-page">
            <div className="flex items-baseline justify-between border-b border-gray-200 pb-1 mb-2">
              <h3 className="font-semibold text-gray-800">Evaluación de ciberseguridad</h3>
              <span className="text-xs text-gray-600">Cumplimiento: <strong>{compSummary.percent}%</strong></span>
            </div>
            <p className="text-[11px] text-gray-500 mb-3">Resolución No. 07 de 11 de mayo de 2026 (AIG, Panamá)</p>
            <table className="w-full text-xs border border-gray-300">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left p-2 border border-gray-300 w-8">#</th>
                  <th className="text-left p-2 border border-gray-300">Control</th>
                  <th className="text-left p-2 border border-gray-300 w-24">Estado</th>
                  <th className="text-left p-2 border border-gray-300">Evidencia / Plan</th>
                </tr>
              </thead>
              <tbody>
                {IT_CYBERSECURITY_CONTROLS.map((ctrl) => {
                  const it = checklist.find(c => c.control_id === ctrl.id) || {};
                  const sMeta = COMPLIANCE_STATUSES.find(s => s.id === it.estado);
                  return (
                    <tr key={ctrl.id} className="break-inside-avoid">
                      <td className="p-2 border border-gray-300 align-top">{ctrl.numero}</td>
                      <td className="p-2 border border-gray-300 align-top">
                        <div className="font-medium text-gray-900">{ctrl.titulo}</div>
                        <div className="text-gray-600 text-[11px]">{ctrl.descripcion}</div>
                      </td>
                      <td className="p-2 border border-gray-300 align-top">
                        {sMeta ? <span className="font-semibold">{sMeta.label}</span> : <span className="text-gray-400 italic">Sin evaluar</span>}
                      </td>
                      <td className="p-2 border border-gray-300 align-top">
                        {it.evidencia && <div><span className="text-gray-500">Evidencia:</span> {it.evidencia}</div>}
                        {it.plan_accion && <div><span className="text-gray-500">Plan:</span> {it.plan_accion}</div>}
                        {it.fecha_objetivo && <div><span className="text-gray-500">Fecha objetivo:</span> {it.fecha_objetivo}</div>}
                        {it.notas && <div className="text-gray-500">{it.notas}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        )}

        {/* Firmas */}
        <section className="mt-10 grid grid-cols-2 gap-12 text-xs">
          <div>
            <div className="border-t border-gray-400 pt-1 text-center text-gray-700">Técnico responsable</div>
          </div>
          <div>
            <div className="border-t border-gray-400 pt-1 text-center text-gray-700">
              Conformidad cliente{visit.recibe_nombre ? ` — ${visit.recibe_nombre}` : ''}
            </div>
          </div>
        </section>

        <p className="text-[10px] text-gray-400 mt-6 text-center print:mt-4">
          Documento generado el {new Date().toLocaleString()}. Para fines internos y de respaldo del servicio prestado.
        </p>
      </div>

      <style>{`
        @media print {
          @page { margin: 14mm; }
          body { background: white; }
        }
        .break-before-page { break-before: page; }
      `}</style>
    </div>
  );
}
